import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QuillEditor } from "../../utils/quill-editor";
import { Formik } from "formik";
import { TextField, Switch, FormControlLabel, Box, Typography } from "@mui/material";
import { toast } from "react-hot-toast";
import { DocumentManagerV6 } from "../../dashboard/Media/DocumentManagerV6";

export default function OfferCoverPage({ offer, formik, onAutoSave }) {
  const { t } = useTranslation();
  const [coverPageActive, setCoverPageActive] = useState(offer?.cover_page_active || false);
  const [coverPageTitle, setCoverPageTitle] = useState(offer?.cover_page_title || "");
  const [coverPageDescription, setCoverPageDescription] = useState(offer?.cover_page_description || "");
  const [coverPageImageId, setCoverPageImageId] = useState(offer?.cover_page_image_id || null);

  useEffect(() => {
    if (offer) {
      setCoverPageActive(offer.cover_page_active || false);
      setCoverPageTitle(offer.cover_page_title || "");
      setCoverPageDescription(offer.cover_page_description || "");
      setCoverPageImageId(offer.cover_page_image_id || null);
    }
  }, [offer]);

  const handleActiveChange = (event) => {
    const newValue = event.target.checked;
    setCoverPageActive(newValue);
    formik.setFieldValue("cover_page_active", newValue);
    setTimeout(() => {
      onAutoSave();
    }, 500);
  };

  const handleTitleChange = (event) => {
    const newValue = event.target.value;
    setCoverPageTitle(newValue);
    formik.setFieldValue("cover_page_title", newValue);
    setTimeout(() => {
      onAutoSave();
    }, 2000);
  };

  const handleDescriptionChange = (value) => {
    setCoverPageDescription(value);
    formik.setFieldValue("cover_page_description", value);
    setTimeout(() => {
      onAutoSave();
    }, 2000);
  };

  const handleDocumentsChange = (documents) => {
    // When documents change, select the first one if available
    if (documents && documents.length > 0) {
      const firstDoc = documents[0];
      if (firstDoc.id !== coverPageImageId) {
        setCoverPageImageId(firstDoc.id);
        formik.setFieldValue("cover_page_image_id", firstDoc.id);
        setTimeout(() => {
          onAutoSave();
        }, 500);
      }
    } else if (coverPageImageId) {
      // If no documents, clear selection
      setCoverPageImageId(null);
      formik.setFieldValue("cover_page_image_id", null);
      setTimeout(() => {
        onAutoSave();
      }, 500);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Forside
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Konfigurer forside for PDF-eksport av tilbudet.
            </p>
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={coverPageActive}
                onChange={handleActiveChange}
                color="primary"
              />
            }
            label={coverPageActive ? "Aktiv" : "Inaktiv"}
          />
        </div>
      </div>
      
      {coverPageActive && (
        <div className="px-6 py-6 space-y-6">
          <div>
            <TextField
              label="Tittel"
              variant="outlined"
              fullWidth
              value={coverPageTitle}
              onChange={handleTitleChange}
              placeholder="Skriv inn tittel for forside..."
              helperText="Tittelen vises på forsiden av PDF-en"
            />
          </div>

          <div>
            <Typography variant="subtitle2" className="mb-2">
              Beskrivelse
            </Typography>
            <QuillEditor
              name="cover_page_description"
              value={coverPageDescription}
              onChange={handleDescriptionChange}
              placeholder="Skriv inn beskrivelse for forside..."
              rows={8}
            />
          </div>

          <div>
            <Typography variant="subtitle2" className="mb-2">
              Forsidebilde
            </Typography>
            <Box className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <DocumentManagerV6
                module="offer"
                moduleParam={offer?.id}
                allowMultiple={false}
                onDocumentsChange={handleDocumentsChange}
                mode="inline"
                showUpload={true}
                showExisting={true}
                allowDelete={true}
                allowEdit={true}
                disabled={!offer?.id}
              />
              {coverPageImageId && offer?.cover_page_image && (
                <Box className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                  <Typography variant="caption" className="text-gray-700 font-medium">
                    Valgt forsidebilde: {offer.cover_page_image.title || "Ingen tittel"}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500 block mt-1">
                    Det første bildet du laster opp vil bli brukt som forsidebilde
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="caption" className="text-gray-500 mt-2 block">
              Bildet vil vises på hele forsiden i PDF-en
            </Typography>
          </div>
        </div>
      )}

      {!coverPageActive && (
        <div className="px-6 py-6">
          <Typography variant="body2" className="text-gray-500">
            Aktiver forside for å konfigurere innholdet.
          </Typography>
        </div>
      )}
    </div>
  );
}

